import React from "react"
import { EditorJsMenu, MenuNode } from "@/ui/EditorJsMenu"

type BaseConfig = {
  registeredToolKeys?: string[]
  createPortal: (
    blockId: string,
    node: React.ReactNode,
    container: HTMLElement,
  ) => React.ReactPortal
  removePortal: (blockId: string) => void
}

type BuildMenuArgs<C extends BaseConfig> = {
  config: C
  isAvailable: (toolName?: string) => boolean
}

export const createMenuTool = <C extends BaseConfig>(opts: {
  title: string
  icon?: string
  buildMenu: (args: BuildMenuArgs<C>) => MenuNode[]
}) => {
  const { title, icon, buildMenu } = opts

  return class ReactMenuTool {
    static get toolbox() {
      return {
        title,
        icon:
          icon ||
          '<svg width="20" height="20" viewBox="0 0 20 20"><rect x="2" y="3" width="16" height="3" rx="1"/><rect x="2" y="9" width="10" height="3" rx="1"/><rect x="2" y="15" width="6" height="3" rx="1"/></svg>',
      }
    }

    static get isReadOnlySupported() {
      return true
    }

    private api: any
    private config: C
    private blockId!: string
    private wrapper!: HTMLElement

    constructor({ api, config }: { api: any; config: C }) {
      this.api = api
      this.config = config
    }

    private isAvailable = (toolName?: string) =>
      !!toolName && (this.config.registeredToolKeys || []).includes(toolName)

    render() {
      this.wrapper = document.createElement("div")
      this.wrapper.className = "editorjs-react-menu"

      queueMicrotask(() => {
        const idx = this.api.blocks.getCurrentBlockIndex()
        const block = this.api.blocks.getBlockByIndex
          ? this.api.blocks.getBlockByIndex(idx)
          : null
        this.blockId =
          block?.id ||
          `menu-${Date.now()}-${Math.random().toString(36).slice(2)}`

        const nodes = buildMenu({
          config: this.config,
          isAvailable: this.isAvailable,
        })

        this.config.createPortal(
          this.blockId,
          <EditorJsMenu
            root={nodes}
            onSelect={(toolName, data) => this.insertTool(toolName, data)}
          />,
          this.wrapper,
        )
      })

      return this.wrapper
    }

    save() {
      return {}
    }

    private insertTool(toolName: string, data?: any) {
      const myIndex = this.api.blocks.getCurrentBlockIndex()
      const insertIndex =
        typeof myIndex === "number" && myIndex >= 0
          ? myIndex + 1
          : this.api.blocks.getBlocksCount()

      try {
        this.api.blocks.insert(toolName, data || {}, {}, insertIndex, true)
        if (typeof myIndex === "number" && myIndex >= 0) {
          this.api.blocks.delete(myIndex)
        }
        if (this.blockId) this.config.removePortal(this.blockId)
      } catch (e) {
        console.error(`[ReactMenuTool] Failed to insert "${toolName}"`, e)
      }
    }
  }
}
